import { createAsyncThunk } from '@reduxjs/toolkit'
import toCamel from 'lodash-humps'
import {
  createFetchAsyncThunk,
  csrfToken,
  toSnake,
  throwErrorForErrorResponse,
  throwTextErrorForErrorResponse,
  errorHandler,
  ErrorPayload
} from '../common'
import { closeModal } from './mediaSlice'
import type { MediaState } from './types'

type ThunkConfig = {
  state: { common: { baseUri: string }; media: MediaState }
  rejectValue: ErrorPayload
}

type MediaIdParams = {
  id: number | string
} & Record<string, unknown>

export const fetchMedia = createFetchAsyncThunk('media/fetchMedia', 'media')

export const fetchMediaEngineStats = createAsyncThunk<unknown, { mediaId: number | string }, ThunkConfig>(
  'media/fetchMediaEngineStats',
  async (params, { getState, rejectWithValue }) => {
    const { baseUri } = getState().common
    const { mediaId } = params

    return fetch(`${baseUri}media/${mediaId}/engine_stats`, {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(throwTextErrorForErrorResponse)
      .then((response) => response.json())
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
  }
)

export const createMedia = createAsyncThunk<unknown, Record<string, unknown>, ThunkConfig>(
  'media/createMedia',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}media`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().media
        dispatch(fetchMedia({ page: currentPage, sort }))
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const modifyMedia = createAsyncThunk<unknown, MediaIdParams, ThunkConfig>(
  'media/modifyMedia',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { id } = params

    return fetch(`${baseUri}media/${id}`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().media
        dispatch(fetchMedia({ page: currentPage, sort }))
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const deleteMedia = createAsyncThunk<unknown, MediaIdParams, ThunkConfig>(
  'media/deleteMedia',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { id } = params

    return fetch(`${baseUri}media/${id}`, {
      method: 'delete',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().media
        dispatch(fetchMedia({ page: currentPage, sort }))
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)
